import { Link } from "react-router-dom";
import { Mail, MapPin, ArrowRight, Building2, Truck, FileCheck } from "lucide-react";
import { BRAND } from "@/data/content";
import { useT } from "@/i18n/i18n";

export default function Contact() {
    const tr = useT();
    const TILES = [
        { icon: Building2, label: tr("contact.tile.operation"), value: BRAND.location, key: "operation" },
        { icon: Mail, label: tr("contact.tile.email"), value: BRAND.email, href: `mailto:${BRAND.email}`, key: "email" },
        { icon: Truck, label: tr("contact.tile.logistics"), value: tr("contact.tile.logistics_value"), key: "logistics" },
        { icon: FileCheck, label: tr("contact.tile.documentation"), value: tr("contact.tile.docs_value"), key: "documentation" },
    ];

    const SUBJECTS = [
        tr("contact.subject.liquid"),
        tr("contact.subject.granulate"),
        tr("contact.subject.lawns"),
        tr("contact.subject.distribution"),
    ];

    const LOGISTICS_LINES = [
        "Pallet · 25 kg & 20 kg PE bags",
        "Big-bag · 1 000 kg",
        "IBC · 1 000 L liquids",
        "Drum · 200 L liquids",
        "Container · LCL & FCL",
        "Express courier · samples",
    ];

    return (
        <div className="pt-12 pb-32" data-testid="contact-page">
            <section className="max-w-7xl mx-auto px-5 lg:px-8">
                <Link to="/" className="eyebrow inline-flex items-center gap-2 text-slate-400 hover:text-white" data-testid="contact-back">
                    {tr("cta.back_brand")}
                </Link>
                <div className="grid lg:grid-cols-12 gap-10 mt-5">
                    <div className="lg:col-span-7">
                        <div className="eyebrow">{tr("page.contact.eyebrow")}</div>
                        <h1 className="mt-3 font-serif text-5xl sm:text-7xl leading-[0.95] tracking-tight">
                            {tr("page.contact.title")}
                        </h1>
                        <p className="mt-6 text-slate-400 text-lg leading-relaxed max-w-2xl">
                            {tr("contact.body")}
                        </p>

                        <div className="mt-10 grid sm:grid-cols-2 gap-4">
                            {TILES.map((t) => {
                                const Wrap = t.href ? "a" : "div";
                                return (
                                    <Wrap
                                        key={t.key}
                                        {...(t.href ? { href: t.href } : {})}
                                        className="rounded-2xl border border-white/10 bg-[#0A1628]/60 p-5 flex items-start gap-4 lift-card"
                                        data-testid={`contact-tile-${t.key}`}
                                    >
                                        <div className="rounded-lg w-11 h-11 flex items-center justify-center bg-kelp-600/15 border border-kelp-500/30 shrink-0">
                                            <t.icon className="h-5 w-5 text-kelp-400" />
                                        </div>
                                        <div>
                                            <div className="eyebrow">{t.label}</div>
                                            <div className="font-serif text-xl mt-1 break-words">{t.value}</div>
                                        </div>
                                    </Wrap>
                                );
                            })}
                        </div>
                    </div>

                    <div className="lg:col-span-5">
                        <div className="rounded-3xl border border-white/10 bg-gradient-to-br from-kelp-700/30 to-ink-700/40 p-8" data-testid="contact-mailbox">
                            <div className="eyebrow">{tr("contact.direct_mailbox")}</div>
                            <div className="font-serif text-4xl mt-3 leading-tight break-words">
                                <a href={`mailto:${BRAND.email}`} className="hover:text-kelp-300 transition-colors" data-testid="contact-email-large">
                                    {BRAND.email}
                                </a>
                            </div>
                            <div className="mt-3 inline-flex items-center gap-2 text-slate-300 text-sm">
                                <MapPin className="h-4 w-4 text-kelp-400" /> {BRAND.location}
                            </div>

                            <div className="mt-8">
                                <div className="eyebrow mb-3">{tr("contact.quick_subjects")}</div>
                                <div className="space-y-2">
                                    {SUBJECTS.map((s) => (
                                        <a
                                            key={s}
                                            href={`mailto:${BRAND.email}?subject=${encodeURIComponent(s)}`}
                                            className="flex items-center justify-between rounded-lg border border-white/10 bg-black/20 px-4 py-3 text-sm text-slate-200 hover:bg-white/5 hover:text-white transition-colors"
                                            data-testid={`contact-subject-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                        >
                                            <span>{s}</span>
                                            <ArrowRight className="h-4 w-4 text-kelp-400" />
                                        </a>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="mt-20 rounded-3xl border border-white/10 bg-[#040912] p-10" data-testid="contact-export">
                    <div className="grid md:grid-cols-2 gap-8 items-center">
                        <div>
                            <div className="eyebrow">{tr("contact.export.eyebrow")}</div>
                            <h2 className="font-serif text-3xl mt-3 leading-tight">
                                {tr("contact.export.title")}
                            </h2>
                            <p className="mt-4 text-slate-400 leading-relaxed">
                                {tr("contact.export.body")}
                            </p>
                        </div>
                        <ul className="space-y-3 text-sm text-slate-300">
                            {LOGISTICS_LINES.map((l) => (
                                <li key={l} className="flex items-center gap-3 rounded-lg border border-white/5 bg-[#0A1628]/40 px-4 py-3">
                                    <span className="h-2 w-2 rounded-full bg-kelp-500" />
                                    <span>{l}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>
        </div>
    );
}
